/* ══════════════════════════════════════════════════════════════════════
   NETWORK GRAPH — visual encoding.

   Everything the canvas draws with lives here: node artwork, per-theme
   surfaces, the vis-network option block, and the model -> vis-node
   mapping. The component next door owns behaviour only, so a restyle
   never means touching the physics or the expand logic.
   ══════════════════════════════════════════════════════════════════════ */
import type { Edge, Node, Options } from "vis-network";
import { GLOW_CORE_BLUR, GLOW_HALO_BLUR } from "@/app/components/ChartsComponent/chartUtils";
import {
  pagNetKindColor,
  pagNetRiskColor,
  type PagNetEdge,
  type PagNetNode,
  type PagNetNodeType,
} from "../../data/pagTypes";

/** vis Node plus the two extras hover uses. `zIndex` is not in vis's own
 *  type; it is carried on the item and read by the draw order. */
export type NetVisNode = Node & { zIndex?: number };
export type NetVisEdge = Edge & { id: string; from: string; to: string };

/* ── Dimensions ─────────────────────────────────────────────────────── */

export const NODE_SIZE = 22;
/** Radius of the ring inside the 46-unit artwork box. One unit under the
 *  20 it could be, because the baked core of the glow bleeds ~2.25 units
 *  past the stroke and the viewBox clips anything wider: at r=20 the outer
 *  stroke edge sits 2 units from the border and the halo squares off. */
const RING_RADIUS = 19;

/** Ring weight, which is now the only thing separating a node that opens
 *  from a leaf. It used to be a tinted fill; with the artwork hollow the
 *  affordance moves onto the border, where the glow already lives. */
const RING_WIDTH = { openable: 2.6, leaf: 1.5 } as const;
/** Resting node opacity, so hover has something to lift out of. */
export const NODE_OPACITY = 0.88;
/** How far a freshly revealed child is seeded from its parent. Small on
 *  purpose: the children grow outward from the click instead of flying in
 *  from the origin, which is most of what makes an expand feel calm. */
export const SPAWN_RADIUS = 60;
/** Canvas label budget. The full value always stays in the tooltip. */
export const LABEL_MAX = 11;

/**
 * Wide halo of the neon, in one node's own hue.
 *
 * A canvas shadow rather than more artwork, for two reasons. It is drawn
 * OUTSIDE the image's box, so unlike an SVG filter it cannot be clipped by
 * the viewBox. And vis calls disableShadow before it draws the label, so the
 * glow provably stays off the text -- which is the rule chartUtils spells
 * out: the neon belongs on the marks, never on the type.
 *
 * Centred and unoffset: this is a glow, not a drop shadow. Opacity rides
 * along for free -- vis fades shadowColor with the node's own opacity, so
 * hover lifting 0.88 to 1 brightens the halo with it.
 */
const glowShadow = (color: string) => ({
  enabled: true,
  color,
  size: GLOW_HALO_BLUR,
  x: 0,
  y: 0,
});

/* Connectivity had to carry further than it did. At 0.4 these were sub-
   pixel hairlines that antialiasing washed out on both canvases, and the
   unfocused pass at 0.1 opacity was effectively not drawn -- which is what
   reads as "the graph lost its lines". Lit edges are now a real stroke and
   the dim pass is quiet rather than absent, so the focus contrast the
   drill-down depends on survives without anything vanishing. */
export const EDGE_WIDTH = 1.1;
export const EDGE_WIDTH_DIM = 0.8;

/** One easing, used by every camera move: the fit after an expand and the
 *  fit after a fullscreen toggle should feel like the same gesture. */
export const FIT_ANIMATION = {
  duration: 1000,
  easingFunction: "easeInOutQuad",
} as const;

/* ── Surfaces ───────────────────────────────────────────────────────── */

export interface NetSurface {
  /** Which theme this surface IS. Carried here so anything already handed
   *  a surface -- the canvas, the legend the two graphs share -- can reach
   *  the matching node palette without a second prop threaded alongside. */
  isDark: boolean;
  canvas: string;
  nodeBg: string;
  label: string;
  /** An edge touching the focused node. */
  edge: string;
  /** Everything else: quiet, but drawn. */
  edgeDim: string;
  edgeHover: string;
  panel: string;
  panelBorder: string;
  muted: string;
}

/* Node hues move with the theme now too -- see pagNetKindColor. What lives
   here is everything those hues are read AGAINST. */
export const SURFACE: Record<"light" | "dark", NetSurface> = {
  light: {
    isDark: false,
    canvas: "#FFFFFF",
    nodeBg: "#FFFFFF",
    label: "#334155",
    edge: "#7C8BA1",
    edgeDim: "#C2CBD8",
    edgeHover: "#475569",
    panel: "#F8FAFC",
    panelBorder: "#E2E8F0",
    muted: "#64748B",
  },
  dark: {
    isDark: true,
    canvas: "#0A1120",
    nodeBg: "#111A2C",
    label: "#CBD5E1",
    edge: "rgba(163,178,199,0.78)",
    edgeDim: "rgba(148,163,184,0.30)",
    edgeHover: "#E2E8F0",
    panel: "rgba(13,19,34,0.92)",
    panelBorder: "rgba(255,255,255,0.12)",
    muted: "#94A3B8",
  },
};

/* ── Node artwork ───────────────────────────────────────────────────────
   vis-network can draw a circle with its label inside, or an image with
   the label underneath, but not a circle with an icon inside AND the
   label underneath. So each node type becomes a tiny inline SVG used as
   `shape: "image"`, which buys the icon and puts the label below it. */

const GLYPH: Record<PagNetNodeType, string> = {
  application:
    '<rect x="-7" y="-7" width="6" height="6" rx="1.2"/><rect x="1" y="-7" width="6" height="6" rx="1.2"/><rect x="-7" y="1" width="6" height="6" rx="1.2"/><rect x="1" y="1" width="6" height="6" rx="1.2"/>',
  account:
    '<rect x="-8" y="-6" width="16" height="12" rx="2" fill="none" stroke-width="1.8"/><circle cx="-3.5" cy="-1" r="2.2" fill="none" stroke-width="1.8"/><path d="M2 -2.5h4M2 1.5h4" fill="none" stroke-width="1.8" stroke-linecap="round"/>',
  user: '<circle cx="0" cy="-3" r="3.2" fill="none" stroke-width="1.8"/><path d="M-6 7a6 6 0 0 1 12 0" fill="none" stroke-width="1.8" stroke-linecap="round"/>',
  group:
    '<circle cx="-3" cy="-3.5" r="2.6" fill="none" stroke-width="1.7"/><circle cx="4.5" cy="-3" r="2.1" fill="none" stroke-width="1.7"/><path d="M-8 5.5a5 5 0 0 1 10 0" fill="none" stroke-width="1.7" stroke-linecap="round"/><path d="M3.5 2.5a4 4 0 0 1 5.5 3" fill="none" stroke-width="1.7" stroke-linecap="round"/>',
  role: '<circle cx="-1.5" cy="-3.5" r="3" fill="none" stroke-width="1.8"/><path d="M-7.5 6.5a6 6 0 0 1 9-5" fill="none" stroke-width="1.8" stroke-linecap="round"/><circle cx="4.5" cy="4.5" r="3" fill="none" stroke-width="1.8"/>',
  policy:
    '<path d="M-5.5 -8h7l4.5 4.5V8h-11.5z" fill="none" stroke-width="1.8" stroke-linejoin="round"/><path d="M1.5 -8v4.5h4.5" fill="none" stroke-width="1.8" stroke-linejoin="round"/>',
  /* Stacked sheets — a rollup is "several of these", whatever these are. */
  rollup:
    '<path d="M-7 -5.5h14M-7 -0.5h14M-7 4.5h14" fill="none" stroke-width="2" stroke-linecap="round"/>',
};

/** Ring + glyph as a data URI, hollow: nothing behind either.
 *
 *  Which is what the neon wanted anyway. A canvas shadow follows whatever
 *  opaque silhouette it is given, so a filled disc threw a halo the size
 *  of the disc; with only the ring and the glyph left to catch it, the
 *  glow traces the outline instead. That is the same hollow-mark build the
 *  bar and donut charts use, where the hue is the stroke and the fill is
 *  not there at all. */
export function nodeImage(
  color: string,
  type: PagNetNodeType,
  openable: boolean,
): string {
  // The application glyph is solid squares; the rest are outlined strokes.
  const glyphAttrs =
    type === "application"
      ? `fill="${color}" stroke="none"`
      : `fill="none" stroke="${color}" stroke-linecap="round"`;

  /* Tight core of the neon. The halo is a canvas shadow and needs no room
     here, but the core hugs the stroke and has to live inside the artwork.
     feDropShadow takes a sigma where the CSS filter takes a blur radius,
     hence the halving -- same light, expressed in the other unit.

     The filter id is fixed rather than unique: every node is its own data
     URI, so these are separate documents and ids cannot collide. */
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">` +
    `<filter id="glow" x="-50%" y="-50%" width="200%" height="200%">` +
    `<feDropShadow dx="0" dy="0" stdDeviation="${GLOW_CORE_BLUR / 2}" flood-color="${color}" flood-opacity="1"/>` +
    `</filter>` +
    `<g filter="url(#glow)">` +
    `<circle cx="23" cy="23" r="${RING_RADIUS}" fill="none" stroke="${color}" stroke-width="${
      openable ? RING_WIDTH.openable : RING_WIDTH.leaf
    }"/>` +
    `<g transform="translate(23,23)" ${glyphAttrs}>${GLYPH[type]}</g>` +
    `</g>` +
    `</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/* ── Labels ─────────────────────────────────────────────────────────── */

/** Canvas labels are clipped so a fan-out of forty AWSReservedSSO_* roles
 *  does not become one unreadable smear. Nothing is lost: `title` carries
 *  the full value, and the tooltip is on a 200ms delay. */
export function shortLabel(label: string): string {
  return label.length > LABEL_MAX ? `${label.slice(0, LABEL_MAX)}...` : label;
}

/**
 * The node's own name, in full, and nothing else -- an Application shows
 * its application name, an Account its account name.
 *
 * That is the whole job here: the canvas label is clipped to LABEL_MAX, so
 * the tooltip exists to hand back the value that was cut. It used to build
 * a block of type, risk and metadata marked up with <b> and <br>, which
 * never rendered -- vis assigns a string title with innerText, not
 * innerHTML (Popup.setText), so those tags printed as literal characters in
 * the panel.
 *
 * innerText is also why nothing is escaped on the way out. The markup is
 * gone, and escaping into a text node would show a policy named "A&B" as
 * "A&amp;B" -- the escape would become the bug.
 */
function tooltip(n: PagNetNode): string {
  return n.type === "rollup" ? `${n.label} (${n.count ?? 0})` : n.label;
}

/* ── Model -> canvas ────────────────────────────────────────────────── */

export function toVisNode(
  n: PagNetNode,
  surface: NetSurface,
  childCount: number,
): NetVisNode {
  const color = pagNetKindColor(n.kind, surface.isDark);
  return {
    id: n.id,
    label: n.type === "rollup" ? `${n.label} (${n.count ?? 0})` : shortLabel(n.label),
    title: tooltip(n),
    shape: "image",
    image: nodeImage(color, n.type, childCount > 0),
    size: NODE_SIZE,
    opacity: NODE_OPACITY,
    shadow: glowShadow(color),
    /* Risk rides on the label colour, and only policies carry risk. */
    font: {
      size: 12,
      face: "Montserrat, sans-serif",
      color:
        n.type === "policy"
          ? pagNetRiskColor(n.severity, surface.isDark)
          : surface.label,
      vadjust: 2,
    },
  };
}

export const toVisEdge = (e: PagNetEdge): NetVisEdge => ({
  id: e.id,
  from: e.from,
  to: e.to,
});

/* ── Options ────────────────────────────────────────────────────────────
   `physicsEnabled` is a parameter rather than a constant because every
   setOptions call replays this block: rebuilding it with physics hard-on
   would silently restart the solver on a theme change. */
export function buildOptions(surface: NetSurface, physicsEnabled: boolean): Options {
  return {
    autoResize: true,
    layout: { hierarchical: false },
    edges: {
      smooth: { enabled: true, type: "continuous", forceDirection: "none", roundness: 0.5 },
      color: {
        color: surface.edge,
        hover: surface.edgeHover,
        highlight: surface.edgeHover,
      },
      arrows: { to: { enabled: false } },
      width: EDGE_WIDTH,
      hoverWidth: 0.8,
      arrowStrikethrough: true,
    },
    physics: {
      enabled: physicsEnabled,
      barnesHut: {
        gravitationalConstant: -25000,
        centralGravity: 0.3,
        springLength: 90,
        springConstant: 0.05,
      },
      /* Low on purpose. A high iteration count re-runs a full solve on
         every dataset change, which is the long crawl after an expand. */
      stabilization: { enabled: true, iterations: 200, fit: true },
    },
    interaction: {
      hover: true,
      /* Off: GraphControls draws the zoom cluster now, in the same place
         and the same style as Policy Insights. vis ships its pad as green
         base64 PNGs bottom-right, which is neither. */
      navigationButtons: false,
      tooltipDelay: 200,
      keyboard: false,
    },
    /* Object-shaped rather than `false` so the shadow branch always has
       something to read; every node overrides this with its own hue. */
    nodes: {
      borderWidth: 0,
      shadow: { enabled: true, size: GLOW_HALO_BLUR, x: 0, y: 0 },
    },
  };
}

/* ── Chrome restyle ─────────────────────────────────────────────────────
   vis-network ships its nav/zoom buttons as green base64 PNGs with a green
   hover glow, which does not belong in this palette. The images cannot be
   swapped without forking the CSS, so they are desaturated and re-lit with
   a filter. Scoped to this component's wrapper so no other vis instance is
   affected. */
export const NAV_CSS = `
.pag-net .vis-navigation .vis-button {
  filter: grayscale(1) brightness(0.5);
  opacity: 0.65;
  transition: opacity 0.15s ease, filter 0.15s ease;
}
.pag-net .vis-navigation .vis-button:hover {
  opacity: 1;
  filter: grayscale(1) brightness(0.28);
  box-shadow: none;
}
.pag-net .vis-navigation .vis-button:active { box-shadow: none; }
.pag-net-dark .vis-navigation .vis-button {
  filter: grayscale(1) brightness(2.4);
  opacity: 0.55;
}
.pag-net-dark .vis-navigation .vis-button:hover {
  opacity: 1;
  filter: grayscale(1) brightness(3.2);
}
.pag-net .vis-tooltip {
  font-family: Montserrat, sans-serif !important;
  font-size: 11px !important;
  line-height: 1.5 !important;
  border-radius: 6px !important;
  padding: 6px 9px !important;
  white-space: normal !important;
  max-width: 320px !important;
  overflow-wrap: break-word !important;
}
`;
